import React, { useState, useEffect } from 'react';
import { getClients, createClient, updateClient, deleteClient, getPays } from '../api';
import SearchSelect from './SearchSelect';
import ConfirmModal from './ConfirmModal';

function Clients() {
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'FR',
    siren: '',
    siret: '',
    tva_intracommunautaire: '',
    identifiant_peppol: '',
    cadre_facturation: '',
    code_service_chorus: '',
    identifiant_peppol_scheme: '',
  });
  const [alert, setAlert] = useState(null);
  const [paysOptions, setPaysOptions] = useState([]);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    loadClients();
    getPays().then(r => {
      const sorted = [...r.data].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
      const france = sorted.find(c => c.value === 'FR');
      const rest = sorted.filter(c => c.value !== 'FR');
      setPaysOptions(france ? [france, ...rest] : sorted);
    }).catch(() => {});
  }, []);

  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data);
    } catch (error) {
      showAlert('Erreur lors du chargement des clients', 'error');
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        showAlert('Client modifié avec succès', 'success');
      } else {
        await createClient(formData);
        showAlert('Client créé avec succès', 'success');
      }
      setShowModal(false);
      setEditingClient(null);
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        adresse: '',
        code_postal: '',
        ville: '',
        pays: 'FR',
        siren: '',
        siret: '',
        tva_intracommunautaire: '',
        identifiant_peppol: '',
        cadre_facturation: '',
        code_service_chorus: '',
        identifiant_peppol_scheme: '',
      });
      loadClients();
    } catch (error) {
      showAlert('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData(client);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirm({
      title: 'Supprimer le client',
      message: 'Ce client sera définitivement supprimé. Cette action est irréversible.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await deleteClient(id);
          showAlert('Client supprimé avec succès', 'success');
          loadClients();
        } catch (error) {
          showAlert('Erreur lors de la suppression', 'error');
        }
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Clients</h2>
        <button className="btn btn-primary" onClick={() => { setShowModal(true); setEditingClient(null); setFormData({ nom: '', email: '', telephone: '', adresse: '', code_postal: '', ville: '', pays: 'FR', siren: '', siret: '', tva_intracommunautaire: '', identifiant_peppol: '', cadre_facturation: '', code_service_chorus: '', identifiant_peppol_scheme: '' }); }}>
          + Nouveau Client
        </button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Ville</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>Aucun client</td>
              </tr>
            ) : (
              clients.map(client => (
                <tr key={client.id}>
                  <td>{client.nom}</td>
                  <td>{client.email || '-'}</td>
                  <td>{client.telephone || '-'}</td>
                  <td>{client.ville || '-'}</td>
                  <td>
                    <button className="btn btn-secondary" onClick={() => handleEdit(client)} style={{ marginRight: '5px' }}>
                      Modifier
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDelete(client.id)}>
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Modifier le client' : 'Nouveau client'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Code postal</label>
                  <input
                    type="text"
                    value={formData.code_postal}
                    onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Ville</label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Pays</label>
                  <SearchSelect
                    options={paysOptions}
                    value={formData.pays}
                    onChange={(v) => setFormData({ ...formData, pays: v || 'FR' })}
                    placeholder="Pays..."
                    isClearable={false}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>SIREN <span style={{ color: 'var(--color-primary)', fontSize: '12px' }}>requis e-facture</span></label>
                  <input
                    type="text"
                    maxLength={9}
                    placeholder="9 chiffres"
                    value={formData.siren}
                    onChange={(e) => setFormData({ ...formData, siren: e.target.value.replace(/\D/g, '') })}
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>SIRET</label>
                  <input
                    type="text"
                    maxLength={14}
                    placeholder="14 chiffres"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value.replace(/\D/g, '') })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>N° TVA intracommunautaire</label>
                <input
                  type="text"
                  placeholder="FR12345678901"
                  value={formData.tva_intracommunautaire}
                  onChange={(e) => setFormData({ ...formData, tva_intracommunautaire: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Identifiant Peppol (optionnel)</label>
                <input
                  type="text"
                  placeholder="SIRET ou identifiant Peppol du client"
                  value={formData.identifiant_peppol || ''}
                  onChange={(e) => setFormData({ ...formData, identifiant_peppol: e.target.value })}
                />
                <p className="form-help">Adresse de routage électronique pour la réception de factures via le PPF. Laissez vide pour utiliser le SIRET.</p>
              </div>
              <div className="form-group">
                <label>Schéma de l'identifiant (schemeID)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="0225"
                  value={formData.identifiant_peppol_scheme || ''}
                  onChange={(e) => setFormData({ ...formData, identifiant_peppol_scheme: e.target.value.replace(/\D/g, '') })}
                />
                <p className="form-help">Vide = 0225 (PPF Annuaire). Utiliser 0224 pour une adresse issue de l'annuaire Chorus Pro.</p>
              </div>
              <div className="form-group">
                <label>Type d'acheteur par défaut</label>
                <select
                  value={formData.cadre_facturation || ''}
                  onChange={(e) => setFormData({ ...formData, cadre_facturation: e.target.value })}
                >
                  <option value="">Entreprise privée (B2B)</option>
                  <option value="A1">Acheteur public (Chorus Pro)</option>
                </select>
                <p className="form-help">Pré-coche « service public » sur les nouvelles factures de ce client. Ne modifie pas les factures existantes : le cadre BT-23 du XML vient de la case cochée sur chaque facture.</p>
              </div>
              <div className="form-group">
                <label>Code service Chorus Pro</label>
                <input
                  type="text"
                  placeholder="Ex : GUIDES"
                  value={formData.code_service_chorus || ''}
                  onChange={(e) => setFormData({ ...formData, code_service_chorus: e.target.value })}
                />
                <p className="form-help">Code du service de l'acheteur public, transmis dans la référence acheteur (BT-10) du Factur-X.</p>
              </div>
              <div className="actions">
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Modifier' : 'Créer'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'primary'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

export default Clients;

